import type { BadgeType, CouponItem } from './types';
import { ItemStatus } from './types';

export type UIStatus = 'NOT_STARTED' | 'ACTIVE' | 'EXPIRING' | 'EXPIRED' | 'USED';

/**
 * Step 2：状态收敛 Adapter，所有状态判断逻辑只存在这一处
 * 优先级：USED > EXPIRED > NOT_STARTED > EXPIRING > ACTIVE
 */
export function getUIStatus(item: CouponItem): UIStatus {
  const now = Date.now();

  if (item.status === ItemStatus.USED) return 'USED';
  if (item.status === ItemStatus.EXPIRED || (item.expire_at > 0 && now > item.expire_at * 1000)) return 'EXPIRED';
  if (item.effective_at > 0 && now < item.effective_at * 1000) return 'NOT_STARTED';
  if (item.is_near_expire) return 'EXPIRING';
  return 'ACTIVE';
}

/**
 * Step 3：扩展后的 UI 数据结构
 */
export interface UIItem extends CouponItem {
  uiStatus: UIStatus;
  /** 倒计时目标时间戳（ms），null 表示不展示倒计时 */
  countdownTarget: number | null;
  /** 角标类型，由状态和 is_red_dot 共同决定 */
  badge: BadgeType;
}

/**
 * Step 3：构建 UIItem，组件不参与业务计算
 */
export function buildUIItem(item: CouponItem): UIItem {
  const uiStatus = getUIStatus(item);

  const countdownTarget = (() => {
    if (uiStatus === 'ACTIVE' || uiStatus === 'EXPIRING') {
      return item.expire_at > 0 ? item.expire_at * 1000 : null;
    }
    if (uiStatus === 'NOT_STARTED') {
      return item.effective_at * 1000;
    }
    return null;
  })();

  const badge: BadgeType = uiStatus === 'EXPIRING' ? 'expiring' : item.is_red_dot ? 'new' : null;

  return { ...item, uiStatus, countdownTarget, badge };
}

/**
 * Step 4：UI 展示配置，避免组件内 if-else
 */
export const UI_CONFIG: Record<UIStatus, {
  /** 按钮文案的 i18n key */
  buttonKey: string;
  /** 按钮是否禁用 */
  disabled: boolean;
  /** 是否展示倒计时 */
  showCountdown: boolean;
  /** 禁用态是否使用暗色样式（NOT_STARTED 保留 default 配色，USED/EXPIRED 使用暗色） */
  dimmed: boolean;
}> = {
  ACTIVE: { buttonKey: 'use', disabled: false, showCountdown: true, dimmed: false },
  EXPIRING: { buttonKey: 'use', disabled: false, showCountdown: true, dimmed: false },
  NOT_STARTED: { buttonKey: 'unavailable', disabled: true, showCountdown: true, dimmed: false },
  EXPIRED: { buttonKey: 'expired', disabled: true, showCountdown: false, dimmed: true },
  USED: { buttonKey: 'used', disabled: true, showCountdown: false, dimmed: true },
};
